import type { RouteRecordRaw, RouteRecordRedirectOption } from 'vue-router'

import { keys } from '@/app/i18n'
import { useAuthStore } from '@/app/stores/auth'

/**
 * The documents module's routes (WP-04, wp/documents §3). Paths and names from the route manifest (D-15); the legacy
 * paths (`/:software/create-document/`, `/:software/edit-document/:id` — mailed by the API, §3 —, …) redirect here
 * through `src/app/router/legacy-redirects.ts`. Create / edit pages need Documents write (D-21); a Freelancer passes
 * every ACL check and sees a document they may not change read-only (OPEN-1 (a)).
 *
 * Document templates were dropped (2026-10-27, owner's call): no `/documents/templates` routes, no template mode in
 * the list and form views; the legacy template paths are dropped in the route manifest (no redirect, 404).
 * Document groups were dropped too (2026-10-28): their paths redirect to the list (below).
 */
const documentsCrumb = { text: keys.t('documents.list.title.documents'), to: { name: 'documents' } }

// "Documents" alone leads to the list: the legacy "Document List" crumb in between said the same twice (2026-10).
const documentCrumbs = [documentsCrumb, { text: keys.t('documents.edit.title.document'), active: true }]

/** Where the dropped document-group paths land: the Documents list of the same software. */
const toDocuments: RouteRecordRedirectOption = (to) => ({
  name: 'documents',
  params: { software: to.params.software },
})

const base = { software: 'common', resource: 'documents' } as const
const write = { ...base, action: 'write' } as const

const routes: RouteRecordRaw[] = [
  {
    path: '/:software/documents',
    name: 'documents',
    component: () => import('./views/DocumentListView.vue'),
    meta: {
      ...base,
      pageTitle: keys.t('documents.list.title.documents'),
      breadcrumb: [{ text: keys.t('documents.list.title.documents'), active: true }],
    },
  },
  {
    // The manifest's create path (and the legacy `/create-document/` behind it) opens the redesigned add page, with
    // its query (`?assignee=`, `?from=`); the classic form only edits since 2026-10 (owner's call).
    path: '/:software/documents/new',
    name: 'create-document',
    redirect: (to) => ({ name: 'add-document', params: to.params, query: to.query }),
    meta: { ...write, navActiveLink: 'documents' },
  },
  {
    // The redesigned add page (2026-10): file first, several files at once, context defaults — every "create a
    // document" link of the app lands here.
    path: '/:software/documents/add',
    name: 'add-document',
    component: () => import('./views/DocumentAddView.vue'),
    meta: {
      ...write,
      navActiveLink: 'documents',
      pageTitle: keys.t('documents.add.title.page'),
      breadcrumb: [documentsCrumb, { text: keys.t('documents.add.title.page'), active: true }],
    },
  },
  {
    // The document's page (2026-10-31, the record layout): Client and Staff with Documents read. A Freelancer keeps
    // the edit route, which shows them their read page and the confirmation banner.
    path: '/:software/documents/:id(\\d+)',
    name: 'document-view',
    component: () => import('./views/DocumentRecordView.vue'),
    beforeEnter: (to) =>
      useAuthStore().userType === 'Freelancer' ? { name: 'edit-document', params: to.params, query: to.query } : true,
    meta: {
      ...base,
      navActiveLink: 'documents',
      pageTitle: keys.t('documents.edit.title.document'),
      breadcrumb: documentCrumbs,
    },
  },
  {
    path: '/:software/documents/:id(\\d+)/edit',
    name: 'edit-document',
    component: () => import('./views/DocumentEditView.vue'),
    meta: {
      ...write,
      navActiveLink: 'documents',
      pageTitle: keys.t('documents.edit.title.editDocument'),
      breadcrumb: documentCrumbs,
    },
  },
  // Document groups were dropped (2026-10-28, owner's call — 17 groups in 7 workspaces, the one in real use repeated a
  // document type): the manifest's paths stay as redirects to the list. The API, the tables and the documents' group
  // links are untouched (the edit form keeps sending them), so the decision can be undone.
  { path: '/:software/document-groups', name: 'document-groups', redirect: toDocuments },
  { path: '/:software/document-groups/new', name: 'create-document-group', redirect: toDocuments },
  { path: '/:software/document-groups/:id(\\d+)/edit', name: 'edit-document-group', redirect: toDocuments },
]

export default routes
